import type { Frame, ImageFit } from '../document/types.ts'

export type Placement = { xMm: number; yMm: number; wMm: number; hMm: number }

/**
 * Position des Bildes innerhalb seines Rahmens (mm, relativ zur Rahmen-Ecke).
 * cover: Bild füllt den Rahmen (Basis-Skalierung = max), contain: passt hinein (min).
 * `fit.scale` vergrößert zusätzlich; `offsetX/Y` (0..1) verschieben im verfügbaren
 * Spielraum, 0.5 = zentriert. Editor-Surface und Druck nutzen exakt diese Funktion.
 */
export function computeImagePlacement(frame: Pick<Frame, 'wMm' | 'hMm'>, source: { width: number; height: number }, fit: ImageFit): Placement {
  const fw = Math.max(0, frame.wMm)
  const fh = Math.max(0, frame.hMm)
  const sw = Math.max(1, source.width)
  const sh = Math.max(1, source.height)
  if (!(fw > 0) || !(fh > 0)) return { xMm: 0, yMm: 0, wMm: 0, hMm: 0 }

  const base = fit.mode === 'contain' ? Math.min(fw / sw, fh / sh) : Math.max(fw / sw, fh / sh)
  const scale = base * Math.max(1, fit.scale || 1)
  const wMm = sw * scale
  const hMm = sh * scale
  const ox = clamp01(fit.offsetX)
  const oy = clamp01(fit.offsetY)
  return {
    xMm: zero((fw - wMm) * ox),
    yMm: zero((fh - hMm) * oy),
    wMm,
    hMm,
  }
}

/** -0 und Float-Rauschen (< 1e-9) auf 0 ziehen, damit Ausgaben deterministisch bleiben. */
function zero(v: number): number {
  return Math.abs(v) < 1e-9 ? 0 : v
}

/** Deckt das platzierte Bild den Rahmen vollständig ab? (cover + kein Rand sichtbar) */
export function placementCoversFrame(frame: Pick<Frame, 'wMm' | 'hMm'>, placement: Placement, toleranceMm = 0.05): boolean {
  return placement.xMm <= toleranceMm
    && placement.yMm <= toleranceMm
    && placement.xMm + placement.wMm >= frame.wMm - toleranceMm
    && placement.yMm + placement.hMm >= frame.hMm - toleranceMm
}

/** Sichtbarer Ausschnitt des Quellbilds in Pixeln (für die effektive dpi). */
export function visibleSourcePx(frame: Pick<Frame, 'wMm' | 'hMm'>, source: { width: number; height: number }, fit: ImageFit): { widthPx: number; heightPx: number } {
  const p = computeImagePlacement(frame, source, fit)
  if (!(p.wMm > 0) || !(p.hMm > 0)) return { widthPx: 0, heightPx: 0 }
  const pxPerMm = source.width / p.wMm
  const visibleW = Math.min(frame.wMm, p.wMm)
  const visibleH = Math.min(frame.hMm, p.hMm)
  return { widthPx: visibleW * pxPerMm, heightPx: visibleH * pxPerMm }
}

function clamp01(v: number): number {
  return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0.5
}
